import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, Megaphone, Loader2, Play, Pause, X as XIcon, FlaskConical, RefreshCw, ShieldCheck,
  ArrowLeft, Search, Users, ImagePlus, Trash2, Clock, Smartphone, CheckCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/misc';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useI18n, useT } from '@/lib/i18n';
import { apiFetch, getProject } from '@/lib/api';
import { useProjects } from '@/components/ProjectSwitcher';
import { isOfficial } from '@/lib/projects';
import { useFreeQuotaToast } from '@/lib/free-quota';
import { formatPhone } from '@/lib/phone';
import type { Contact, Taxon } from '@/lib/contacts';

interface Campaign {
  id: string; name: string; status: string; pacing_profile: string;
  rail: string; start_at?: string; paused_reason?: string; created_at?: string;
  /** Preenchido pela API quando a campanha espera a janela de envio (08h–21h BRT). */
  waiting?: string; starts_at?: string;
}
interface Stats {
  total: number; pending: number; sent: number; failed: number;
  suppressed: number; delivered: number; read: number;
}
interface DryRun {
  stats: Stats; variations: number; numbers_available: number;
  missing_variables: string[]; estimated_human: string; warnings: string[];
}

// Chave, não rótulo: o status aparece no chip do filtro e no selo do cartão, e os dois
// têm de falar o idioma da conta. Resolvido com t() na hora de desenhar.
const STATUS_KEY: Record<string, string> = {
  draft: 'campaigns.statusRascunho', scheduled: 'campaigns.statusAgendada',
  running: 'campaigns.statusAndamento', paused: 'campaigns.statusPausada',
  completed: 'campaigns.statusConcluida', canceled: 'campaigns.statusCancelada',
};
const STATUS_TONE: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  scheduled: 'bg-blue-100 text-blue-700',
  running: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  completed: 'bg-zinc-200 text-zinc-700',
  canceled: 'bg-red-100 text-red-700',
};

const taClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

/** Humaniza segundos → "~2h 15min" / "~8min" / "instantâneo". */
function humanTime(sec: number): string {
  if (!sec || sec <= 0) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  if (h > 0) return `~${h}h${m > 0 ? ` ${m}min` : ''}`;
  if (m > 0) return `~${m}min`;
  return '~1min';
}

/** Render de preview: resolve spintax {a|b|c}→a; mantém variáveis {nome} visíveis. */
function renderPreview(text: string): string {
  return text.replace(/\{([^{}]*\|[^{}]*)\}/g, (_, g: string) => g.split('|')[0]);
}

const EDITABLE = ['draft', 'scheduled'];

/** Janela de envio das campanhas (espelha campaign.DefaultWindow na API). */
const WINDOW_START_HR = 8, WINDOW_END_HR = 21;
/** Hora em Brasília do valor de um <input type=datetime-local> (hora local do navegador). */
function outsideSendWindow(local: string): boolean {
  if (!local) return false;
  const h = Number(new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hourCycle: 'h23', timeZone: 'America/Sao_Paulo' }).format(new Date(local)));
  return h < WINDOW_START_HR || h >= WINDOW_END_HR;
}

function WaitingNote({ text }: { text?: string }) {
  if (!text) return null;
  return <p className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400"><Clock className="size-3 shrink-0" /> {text.charAt(0).toUpperCase() + text.slice(1)}</p>;
}
type View = { mode: 'list' } | { mode: 'create' } | { mode: 'edit'; id: string } | { mode: 'detail'; id: string };

export function Campaigns() {
  const t = useT();
  const qc = useQueryClient();
  const [view, setView] = useState<View>({ mode: 'list' });

  const { data: ent } = useQuery({
    queryKey: ['entitlements'],
    queryFn: async () => (await apiFetch('GET', '/me/entitlements')).data as { plan?: string; has_campaigns?: boolean } | null,
  });
  const needsAddon = !!ent && ent.plan !== 'exempt' && ent.has_campaigns === false;
  const refresh = () => qc.invalidateQueries({ queryKey: ['campaigns'] });

  if (view.mode === 'create' || view.mode === 'edit') {
    return <CampaignBuilder editId={view.mode === 'edit' ? view.id : undefined}
      onBack={() => { refresh(); setView({ mode: 'list' }); }}
      onDone={(id) => { refresh(); setView({ mode: 'detail', id }); }} />;
  }
  if (view.mode === 'detail') {
    return <CampaignDetail id={view.id} onBack={() => { refresh(); setView({ mode: 'list' }); }} onChange={refresh} />;
  }
  // Clique na lista: não iniciada → editar; iniciada → só visualização.
  const openCampaign = (c: Campaign) =>
    setView(EDITABLE.includes(c.status) ? { mode: 'edit', id: c.id } : { mode: 'detail', id: c.id });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold"><Megaphone className="size-5" /> {t('campaigns.titulo')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('campaigns.subtitlePre')}{' '}
            <span className="font-medium text-foreground">{t('campaigns.subtitleStrong')}</span>{' '}
            {t('campaigns.subtitlePost')}
          </p>
        </div>
        <Button className="w-full shrink-0 sm:w-auto" onClick={() => setView({ mode: 'create' })}>
          <Plus className="size-4" /> Nova campanha
        </Button>
      </div>

      {needsAddon && (
        <div className="flex flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 size-5 shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-semibold text-amber-900">{t('campaigns.addonTitle')}</p>
              <p className="text-sm text-amber-800">{t('campaigns.addonDesc')}</p>
            </div>
          </div>
          <a href="/billing" className="shrink-0"><Button className="w-full sm:w-auto">{t('campaigns.addonCta')}</Button></a>
        </div>
      )}

      <CampaignList onOpen={openCampaign} onCreate={() => setView({ mode: 'create' })} />
    </div>
  );
}

// ---- Listagem em painéis de KPI -------------------------------------------

const LIST_STATUSES = ['all', 'draft', 'scheduled', 'running', 'paused', 'completed', 'canceled'];

function CampaignList({ onOpen, onCreate }: { onOpen: (c: Campaign) => void; onCreate: () => void }) {
  const t = useT();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const { data: camps = [], isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: async (): Promise<Campaign[]> =>
      ((await apiFetch('GET', '/campaigns')).data as { data?: Campaign[] })?.data ?? [],
    refetchInterval: 5000, // mantém os KPIs vivos
  });

  const q = search.trim().toLowerCase();
  const filtered = camps.filter((c) =>
    (status === 'all' || c.status === status) &&
    (!q || (c.name || '').toLowerCase().includes(q)));

  if (isLoading) return <div className="flex flex-col gap-3">{[0, 1].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>;
  if (camps.length === 0) {
    return (
      <Card><CardContent className="flex flex-col items-center gap-3 py-16 text-center">
        <Megaphone className="size-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{t('campaigns.vazia')}</p>
        <Button size="sm" onClick={onCreate}><Plus className="size-4" /> {t('campaigns.criarPrimeira')}</Button>
      </CardContent></Card>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {/* Filtros: busca por título + status */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative sm:max-w-xs sm:flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('campaigns.buscar')} className="pl-8" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {LIST_STATUSES.map((st) => (
            <button key={st} onClick={() => setStatus(st)}
              className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${status === st ? 'border-primary bg-primary/10 text-foreground' : 'border-input text-muted-foreground hover:bg-accent'}`}>
              {st === 'all' ? t('campaigns.statusTodas') : STATUS_KEY[st] ? t(STATUS_KEY[st]) : st}
            </button>
          ))}
        </div>
      </div>

      {/* Lista em linha cheia */}
      {filtered.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">{t('campaigns.semFiltro')}</CardContent></Card>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((c) => <CampaignPanel key={c.id} campaign={c} onOpen={() => onOpen(c)} />)}
        </div>
      )}
    </div>
  );
}

function CampaignPanel({ campaign, onOpen }: { campaign: Campaign; onOpen: () => void }) {
  const t = useT();
  const { data } = useQuery({
    queryKey: ['campaign', campaign.id, 'stats'],
    queryFn: async () => (await apiFetch('GET', `/campaigns/${campaign.id}`)).data as { campaign: Campaign; stats: Stats },
    refetchInterval: campaign.status === 'running' ? 4000 : false,
  });
  const s = data?.stats;
  const total = s?.total ?? 0;
  const done = (s?.sent ?? 0) + (s?.failed ?? 0) + (s?.suppressed ?? 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <button onClick={onOpen} className="group block w-full text-left">
      <Card className="transition-shadow hover:shadow-md">
        <CardContent className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:gap-6">
          {/* Título + status */}
          <div className="flex items-center gap-3 lg:w-64 lg:shrink-0">
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold group-hover:underline">{campaign.name || t('campaigns.semNome')}</p>
              <p className="text-xs text-muted-foreground">{t(campaign.pacing_profile === 'conservative' ? 'campaigns.ritmoCardConservador' : 'campaigns.ritmoCardNormal')}</p>
              <WaitingNote text={campaign.waiting} />
            </div>
            <Badge className={STATUS_TONE[campaign.status] ?? ''}>{STATUS_KEY[campaign.status] ? t(STATUS_KEY[campaign.status]) : campaign.status}</Badge>
          </div>

          {/* Progresso */}
          <div className="lg:flex-1">
            <div className="mb-1 flex justify-between text-xs text-muted-foreground">
              <span>{done}/{total} processados</span><span>{pct}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-4 gap-2 text-center lg:w-96 lg:shrink-0">
            <Kpi label={t('campaigns.kpiEnviados')} value={s?.sent ?? 0} tone="text-emerald-600" />
            <Kpi label={t('campaigns.kpiEntregues')} value={s?.delivered ?? 0} tone="text-sky-600" />
            <Kpi label={t('campaigns.kpiPendentes')} value={s?.pending ?? 0} tone="text-amber-600" />
            <Kpi label={t('campaigns.kpiFalhas')} value={s?.failed ?? 0} tone="text-red-600" />
          </div>
        </CardContent>
      </Card>
    </button>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-md bg-muted/40 py-2">
      <p className={`text-lg font-bold tabular-nums ${tone ?? ''}`}>{value}</p>
      <p className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}

// ---- Builder (nova campanha) ----------------------------------------------

interface Selection { mode: 'ids' | 'all'; ids: Set<string>; total: number; filter: Record<string, unknown>; }
interface VariationTemplate { name: string; language?: string; variables?: string[] }
interface MetaCost { currency: string; category: string; unit_price: number; total: number; daily_limit?: number; days_needed?: number }

/** Quanto a Meta vai cobrar (no cartão do cliente, não na fatura do bZapper) e o ritmo imposto pelo limite diário. */
function MetaCostPanel({ cost }: { cost: MetaCost }) {
  const { t, locale } = useI18n();
  const money = (v: number, d = 2) => new Intl.NumberFormat(locale, { style: 'currency', currency: cost.currency || 'BRL', minimumFractionDigits: d }).format(v);
  return (
    <Card>
      <CardContent className="flex flex-col gap-1.5 p-4 text-sm">
        <p className="text-xs text-muted-foreground">{t('campaigns.metaCost.title')}</p>
        {cost.category === 'service'
          ? <p className="font-semibold">{t('campaigns.metaCost.free')}</p>
          : <p className="text-2xl font-bold tabular-nums">{money(cost.total)}</p>}
        <p className="text-xs text-muted-foreground">
          {t('campaigns.metaCost.detail', { category: t(`dashboard.official.cat.${cost.category}`), unit: money(cost.unit_price, 4) })}
        </p>
        {!!cost.days_needed && cost.days_needed > 1 && (
          <p className="rounded-md bg-amber-500/10 px-2 py-1.5 text-xs text-amber-700 dark:text-amber-400">
            {t('campaigns.metaCost.limit', { limit: cost.daily_limit ?? 0, days: cost.days_needed })}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground">{t('campaigns.metaCost.note')}</p>
      </CardContent>
    </Card>
  );
}
interface VariationData { body: string; media?: { url?: string; type?: string }; template?: VariationTemplate }

/** ISO → valor de <input type=datetime-local> na hora local. */
function toLocalDatetime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function CampaignBuilder({ editId, onBack, onDone }: { editId?: string; onBack: () => void; onDone: (id: string) => void }) {
  const t = useT();
  const onFreeQuota = useFreeQuotaToast();
  const [name, setName] = useState('');
  const [pacing, setPacing] = useState('normal');
  const [variations, setVariations] = useState<string[]>(['', '', '']);
  const [newImage, setNewImage] = useState<{ file: File; url: string } | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null); // imagem já salva (edição)
  const [launch, setLaunch] = useState<'manual' | 'scheduled'>('manual');
  const [startAt, setStartAt] = useState('');
  const [sel, setSel] = useState<Selection>({ mode: 'ids', ids: new Set(), total: 0, filter: {} });
  const [existingCount, setExistingCount] = useState(0);
  const [busy, setBusy] = useState(false);
  // Trilho OFICIAL: a variação é um template aprovado (texto livre não alcança
  // quem não escreveu nas últimas 24h — e campanha é para essa gente).
  const { data: projects = [] } = useProjects();
  const official = isOfficial(projects.find((p) => p.id === getProject()) ?? projects.find((p) => p.is_default));
  const [tpl, setTpl] = useState<VariationTemplate | null>(null);

  // Edição: carrega a campanha e pré-preenche.
  const { data: loaded } = useQuery({
    queryKey: ['campaign', editId, 'edit'],
    queryFn: async () => (await apiFetch('GET', `/campaigns/${editId}`)).data as
      { campaign: Campaign; stats: Stats; variations: VariationData[] },
    enabled: !!editId,
  });
  useEffect(() => {
    if (!loaded) return;
    setName(loaded.campaign.name ?? '');
    setPacing(loaded.campaign.pacing_profile ?? 'normal');
    if (loaded.campaign.start_at) { setLaunch('scheduled'); setStartAt(toLocalDatetime(loaded.campaign.start_at)); }
    const texts = loaded.variations.map((v) => v.body);
    setVariations([texts[0] ?? '', texts[1] ?? '', texts[2] ?? '']);
    setImageUrl(loaded.variations.find((v) => v.media?.url)?.media?.url ?? null);
    setTpl(loaded.variations.find((v) => v.template)?.template ?? null);
    setExistingCount(loaded.stats.total ?? 0);
  }, [loaded]);

  const recipTouched = sel.mode === 'all' || sel.ids.size > 0;
  const count = editId && !recipTouched ? existingCount : (sel.mode === 'all' ? sel.total : sel.ids.size);
  const previewImg = newImage?.url ?? imageUrl;

  const { data: est } = useQuery({
    queryKey: ['campaign-estimate', count, pacing],
    queryFn: async () => (await apiFetch('GET', `/campaigns/estimate?recipients=${count}&pacing=${pacing}`)).data as
      { numbers_available: number; estimated_seconds: number; estimated_human: string },
    enabled: count > 0,
  });

  // No oficial, com o template escolhido: quanto a Meta vai cobrar e quantos dias
  // o limite diário de contatos novos do número exige.
  const { data: metaCost } = useQuery({
    queryKey: ['campaign-meta-cost', count, tpl?.name, tpl?.language],
    queryFn: async (): Promise<MetaCost | null> => {
      const qs = new URLSearchParams({ recipients: String(count), template: tpl?.name ?? '', ...(tpl?.language ? { language: tpl.language } : {}) });
      const r = await apiFetch('GET', `/campaigns/meta-cost?${qs}`);
      return r.ok ? (r.data as MetaCost) : null;
    },
    enabled: official && !!tpl?.name && count > 0,
  });

  // Elegibilidade dos números (painel ilustrativo): status + aquecimento por número.
  const { data: elig } = useQuery({
    queryKey: ['campaign-eligibility'],
    queryFn: async () => (await apiFetch('GET', '/campaigns/eligibility')).data as Eligibility,
    refetchInterval: 30_000,
  });

  const onFile = (f: File | undefined) => {
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(f.type)) { toast.error(t('campaigns.erroImagemTipo')); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error(t('campaigns.erroImagemTamanho')); return; }
    setNewImage({ file: f, url: URL.createObjectURL(f) });
    setImageUrl(null);
  };
  const removeImage = () => { setNewImage(null); setImageUrl(null); };

  const submit = async () => {
    const vars = official
      ? (tpl ? [{ body: '', template: tpl } as Record<string, unknown>] : [])
      : variations.map((b) => b.trim()).filter(Boolean).map((body) => ({ body } as Record<string, unknown>));
    if (!vars.length) { toast.error(t(official ? 'campaigns.erroSemTemplate' : 'campaigns.erroSemVariacao')); return; }
    if (official && tpl?.variables?.some((v) => !v.trim())) { toast.error(t('campaigns.erroVariavelVazia')); return; }
    if (count === 0) { toast.error(t('campaigns.erroSemContato')); return; }
    if (launch === 'scheduled' && !startAt) { toast.error(t('campaigns.erroSemData')); return; }
    setBusy(true);
    try {
      // Imagem: nova → upload; senão mantém a que já estava salva.
      let media: Record<string, unknown> | null = null;
      if (newImage) {
        const fd = new FormData(); fd.append('file', newImage.file);
        const up = await apiFetch('POST', '/campaigns/media', fd);
        if (up.ok && (up.data as { url?: string })?.url) media = { type: 'image', url: (up.data as { url: string }).url };
        else toast.error(t('campaigns.erroUpload'));
      } else if (imageUrl) {
        media = { type: 'image', url: imageUrl };
      }
      const withMedia = media ? vars.map((v) => ({ ...v, media })) : vars;
      const startIso = launch === 'scheduled' && startAt ? new Date(startAt).toISOString() : null;
      const payload = { name, pacing_profile: pacing, start_at: startIso, variations: official ? vars : withMedia };

      let id = editId ?? '';
      if (editId) {
        const res = await apiFetch('PATCH', `/campaigns/${editId}`, payload);
        if (!res.ok) throw new Error((res.data as { message?: string })?.message ?? 'Falha ao salvar');
      } else {
        const res = await apiFetch('POST', '/campaigns', payload);
        if (!res.ok) throw new Error((res.data as { message?: string })?.message ?? 'Falha ao criar');
        id = (res.data as { id: string }).id;
      }
      // Destinatários: no create sempre; no edit só se a seleção foi mexida (substitui).
      if (!editId || recipTouched) {
        const body: Record<string, unknown> = sel.mode === 'all' ? { contact_filter: sel.filter } : { contact_ids: [...sel.ids] };
        if (editId) body.replace = true;
        const addRes = await apiFetch('POST', `/campaigns/${id}/recipients`, body);
        if (!addRes.ok) throw new Error((addRes.data as { message?: string })?.message ?? 'Falha ao salvar destinatários');
      }
      // Agendar: arma como "scheduled" (dispara sozinho no horário).
      if (launch === 'scheduled') {
        const st = await apiFetch('POST', `/campaigns/${id}/start`);
        if (!st.ok) {
          // Franquia do Free esgotada: a campanha fica salva e o aviso traz o upgrade.
          if (onFreeQuota(st)) { onDone(id); return; }
          const code = (st.data as { code?: string })?.code;
          toast.warning(code === 'campaign_needs_warmed_number'
            ? 'Salva! Para agendar de fato, conecte e aqueça ao menos 1 número, depois clique em Agendar no detalhe.'
            : ((st.data as { message?: string })?.message ?? 'Salva, mas não foi possível agendar agora.'));
          onDone(id); return;
        }
        const waiting = (st.data as { waiting?: string })?.waiting;
        if (waiting) toast.warning(`Campanha agendada, mas ${waiting}.`);
        else toast.success(t('campaigns.agendada'));
      } else {
        toast.success(editId ? 'Alterações salvas.' : 'Campanha salva. Clique em Iniciar quando quiser disparar.');
      }
      onDone(id);
    } catch (e) {
      toast.error(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} aria-label={t('campaigns.voltar')}><ArrowLeft className="size-4" /></Button>
        <h1 className="text-lg font-semibold">{editId ? 'Editar campanha' : 'Nova campanha'}</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
        {/* Coluna principal: composição + seleção */}
        <div className="flex flex-col gap-6">
          <Card><CardContent className="flex flex-col gap-4 p-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label>{t('campaigns.nome')}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('campaigns.nomePlaceholder')} />
              </div>
              {official ? (
                <div className="flex flex-col gap-1.5">
                  <Label>{t('campaigns.ritmo')}</Label>
                  <p className="text-xs text-muted-foreground">{t('campaigns.ritmoOficial')}</p>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <Label>{t('campaigns.ritmo')}</Label>
                  <select className={taClass} value={pacing} onChange={(e) => setPacing(e.target.value)}>
                    <option value="conservative">{t('campaigns.ritmoConservador')}</option>
                    <option value="normal">{t('campaigns.ritmoNormal')}</option>
                  </select>
                </div>
              )}
            </div>

            {/* Acionamento */}
            <div className="flex flex-col gap-2">
              <Label>{t('campaigns.acionamento')}</Label>
              <div className="grid gap-2 sm:grid-cols-2">
                <button type="button" onClick={() => setLaunch('manual')}
                  className={`flex items-start gap-2 rounded-md border p-3 text-left text-sm transition-colors ${launch === 'manual' ? 'border-primary bg-primary/5' : 'border-input hover:bg-accent'}`}>
                  <Play className="mt-0.5 size-4 shrink-0" />
                  <span><b>{t('campaigns.manual')}</b><br /><span className="text-xs text-muted-foreground">{t('campaigns.manualDesc')}</span></span>
                </button>
                <button type="button" onClick={() => setLaunch('scheduled')}
                  className={`flex items-start gap-2 rounded-md border p-3 text-left text-sm transition-colors ${launch === 'scheduled' ? 'border-primary bg-primary/5' : 'border-input hover:bg-accent'}`}>
                  <Clock className="mt-0.5 size-4 shrink-0" />
                  <span><b>{t('campaigns.agendar')}</b><br /><span className="text-xs text-muted-foreground">{t('campaigns.agendarDesc')}</span></span>
                </button>
              </div>
              {launch === 'scheduled' && (
                <Input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} className="max-w-xs" />
              )}
              {launch === 'scheduled' && outsideSendWindow(startAt) && (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Esse horário está fora da janela de envio ({String(WINDOW_START_HR).padStart(2, '0')}h–{WINDOW_END_HR}h, horário de Brasília). A campanha fica aguardando e começa na próxima abertura da janela.
                </p>
              )}
            </div>
          </CardContent></Card>

          {official
            ? <TemplateComposer value={tpl} onChange={setTpl} />
            : <MessageComposer variations={variations} setVariations={setVariations} imageUrl={previewImg} onFile={onFile} onRemove={removeImage} />}
          <ContactSelector sel={sel} setSel={setSel} existingCount={editId ? existingCount : undefined} />
        </div>

        {/* Sidebar: dashboard + preview */}
        <div className="flex flex-col gap-6 lg:sticky lg:top-4 lg:self-start">
          <LiveDashboard count={count} pacing={pacing}
            numbers={est?.numbers_available ?? 0} seconds={est?.estimated_seconds ?? 0} />
          {official && metaCost && <MetaCostPanel cost={metaCost} />}
          {!official && <NumbersPanel elig={elig} />}
          <PhonePreview text={official ? '' : renderPreview(variations.find((v) => v.trim()) ?? '')} image={official ? null : previewImg} />
          <Button className="w-full" onClick={submit} disabled={busy || count === 0}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : launch === 'scheduled' ? <Clock className="size-4" /> : <Play className="size-4" />}
            {editId ? ' Salvar alterações' : launch === 'scheduled' ? ' Agendar campanha' : ' Salvar campanha'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- Composer: imagem-cabeçalho + variações de texto ----------------------

// Variáveis do template — vêm do CADASTRO do contato (payload montado no servidor).
const CONTACT_VARS = ['nome', 'email', 'telefone', 'documento', 'cidade', 'estado', 'bairro', 'cep', 'pais'];

function MessageComposer({ variations, setVariations, imageUrl, onFile, onRemove }: {
  variations: string[]; setVariations: (f: (p: string[]) => string[]) => void;
  imageUrl: string | null; onFile: (f: File | undefined) => void; onRemove: () => void;
}) {
  const t = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  const setVar = (i: number, v: string) => setVariations((p) => p.map((x, j) => (j === i ? v : x)));

  // Rastreia o textarea focado + posição do cursor, para inserir a variável ali.
  const taRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const active = useRef<{ idx: number; start: number; end: number }>({ idx: 0, start: 0, end: 0 });
  const track = (i: number) => {
    const el = taRefs.current[i];
    if (el) active.current = { idx: i, start: el.selectionStart ?? el.value.length, end: el.selectionEnd ?? el.value.length };
  };
  const insertVar = (k: string) => {
    const { idx, start, end } = active.current;
    const tok = `{${k}}`;
    setVariations((p) => p.map((x, j) => (j === idx ? x.slice(0, start) + tok + x.slice(end) : x)));
    requestAnimationFrame(() => {
      const el = taRefs.current[idx];
      if (el) { const pos = start + tok.length; el.focus(); el.setSelectionRange(pos, pos); active.current = { idx, start: pos, end: pos }; }
    });
  };

  return (
    <Card><CardContent className="flex flex-col gap-4 p-5">
      <h2 className="text-base font-semibold">{t('campaigns.mensagem')}</h2>

      {/* Imagem-cabeçalho */}
      <div className="flex flex-col gap-1.5">
        <Label>{t('campaigns.imagemCabecalho')} <span className="text-muted-foreground">{t('campaigns.opcional')}</span></Label>
        {imageUrl ? (
          <div className="flex items-center gap-3">
            <img src={imageUrl} alt="" className="size-16 rounded-md border object-cover" />
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={onRemove}>
              <Trash2 className="size-4" /> Remover
            </Button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()}
            className="flex items-center justify-center gap-2 rounded-md border border-dashed py-6 text-sm text-muted-foreground transition-colors hover:bg-accent">
            <ImagePlus className="size-4" /> Adicionar imagem (PNG/JPEG/WebP, até 5 MB)
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])} />
      </div>

      {/* Variações de texto */}
      <div className="flex flex-col gap-2">
        <Label>{t('campaigns.texto')} <span className="text-muted-foreground">{t('campaigns.textoDica')}</span></Label>

        {/* Variáveis disponíveis — clique para inserir no ponto do cursor. */}
        <div className="rounded-md border bg-muted/30 p-3">
          <p className="mb-1.5 text-xs text-muted-foreground">
            <b>{t('campaigns.variaveis')}</b> (do cadastro do contato) — clique para inserir. Se o contato não tiver o campo, sai em branco.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {CONTACT_VARS.map((k) => (
              <button type="button" key={k} onClick={() => insertVar(k)}
                className="rounded-full border border-input bg-background px-2.5 py-0.5 font-mono text-xs text-muted-foreground transition-colors hover:border-primary hover:bg-primary/10 hover:text-foreground">
                {`{${k}}`}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Spintax (rotação anti-bloqueio): <code>{'{Oi|Olá|E aí}'}</code> — sorteia uma opção por envio.
          </p>
        </div>

        {variations.map((v, i) => (
          <textarea key={i} ref={(el) => { taRefs.current[i] = el; }} className={taClass} rows={2} value={v}
            onChange={(e) => { setVar(i, e.target.value); track(i); }}
            onFocus={() => track(i)} onClick={() => track(i)} onKeyUp={() => track(i)} onSelect={() => track(i)}
            placeholder={t('campaigns.variacaoPlaceholder', { n: i + 1 })} />
        ))}
      </div>
    </CardContent></Card>
  );
}

// ---- Composer do trilho oficial: template aprovado + variáveis ---------------

interface ApprovedTemplate {
  name: string; language: string; status: string; variables: number; category: string;
  spec: { body: { text: string }; header?: { type: string; text?: string } };
}

/**
 * No trilho oficial a mensagem de campanha é um template APROVADO: só ele alcança
 * quem não escreveu nas últimas 24h. Cada variável do corpo vira um campo, que
 * aceita texto fixo e {campo} do cadastro do contato — o mesmo vocabulário do
 * texto livre no outro trilho.
 */
function TemplateComposer({ value, onChange }: { value: VariationTemplate | null; onChange: (t: VariationTemplate | null) => void }) {
  const { data: list = [], isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: async () => ((await apiFetch('GET', '/templates')).data as { templates?: ApprovedTemplate[] })?.templates ?? [],
  });
  const approved = list.filter((t) => t.status === 'APPROVED');
  const key = (t: { name: string; language?: string }) => `${t.name}|${t.language ?? ''}`;
  const current = approved.find((t) => value && t.name === value.name && (!value.language || t.language === value.language));
  const vars = value?.variables ?? [];

  const pick = (k: string) => {
    const t = approved.find((x) => key(x) === k);
    onChange(t ? { name: t.name, language: t.language, variables: Array.from({ length: t.variables }, () => '') } : null);
  };
  const setVar = (i: number, v: string) => value && onChange({ ...value, variables: vars.map((x, j) => (j === i ? v : x)) });
  const preview = current
    ? current.spec.body.text.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (_m, _n, off: number) => {
        const idx = (current.spec.body.text.slice(0, off).match(/\{\{/g) ?? []).length;
        return vars[idx] || `{{${idx + 1}}}`;
      })
    : '';

  return (
    <Card><CardContent className="flex flex-col gap-4 p-5">
      <h2 className="text-base font-semibold">Mensagem</h2>
      <p className="text-xs text-muted-foreground">
        Na API oficial, mensagem para quem não falou com você nas últimas 24 horas só sai como <b>template aprovado pela Meta</b>.
      </p>
      {isLoading ? <Skeleton className="h-9 w-full" /> : approved.length === 0 ? (
        <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          Nenhum template aprovado ainda. Crie um em <b>Templates</b> — a Meta responde em até 24 horas.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Label>Template</Label>
          <select className={taClass} value={current ? key(current) : ''} onChange={(e) => pick(e.target.value)}>
            <option value="">Escolha um template aprovado…</option>
            {approved.map((t) => <option key={key(t)} value={key(t)}>{t.name} · {t.language}</option>)}
          </select>
        </div>
      )}
      {current && vars.length > 0 && (
        <div className="flex flex-col gap-2">
          <Label>Variáveis</Label>
          <p className="text-xs text-muted-foreground">
            Texto fixo ou campo do contato — {CONTACT_VARS.slice(0, 3).map((k) => `{${k}}`).join(', ')}… Se o contato não tiver o campo, sai em branco.
          </p>
          {vars.map((v, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-12 shrink-0 font-mono text-xs text-muted-foreground">{`{{${i + 1}}}`}</span>
              <Input value={v} onChange={(e) => setVar(i, e.target.value)} placeholder="{nome}" />
            </div>
          ))}
        </div>
      )}
      {current && (
        <div className="rounded-md border bg-muted/30 p-3 text-sm whitespace-pre-wrap">{preview}</div>
      )}
    </CardContent></Card>
  );
}

// ---- Seletor de contatos (só válidos) -------------------------------------

function ContactSelector({ sel, setSel, existingCount }: { sel: Selection; setSel: (s: Selection) => void; existingCount?: number }) {
  const t = useT();
  const [search, setSearch] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [tab, setTab] = useState<'all' | 'selected'>('all');
  // Guarda os dados de exibição (nome/telefone) dos contatos escolhidos — os ids
  // podem ter sido marcados sob outra busca, então não dá p/ confiar na lista atual.
  const [picked, setPicked] = useState<Map<string, Contact>>(new Map());

  const { data: tagDefs = [] } = useQuery({
    queryKey: ['tags'], queryFn: async (): Promise<Taxon[]> => ((await apiFetch('GET', '/tags')).data as { data?: Taxon[] })?.data ?? [],
  });
  const { data: groupDefs = [] } = useQuery({
    queryKey: ['contact-groups'], queryFn: async (): Promise<Taxon[]> => ((await apiFetch('GET', '/contact-groups')).data as { data?: Taxon[] })?.data ?? [],
  });

  const filter = useMemo(() => {
    const f: Record<string, unknown> = {};
    if (search.trim()) f.search = search.trim();
    if (tags.length) f.tags = tags;
    if (groups.length) f.groups = groups;
    return f;
  }, [search, tags, groups]);

  // SEGURANÇA: status=active fixo — só contatos válidos entram na busca.
  const qs = new URLSearchParams({ status: 'active', limit: '50' });
  if (search.trim()) qs.set('search', search.trim());
  if (tags.length) qs.set('tags', tags.join(','));
  if (groups.length) qs.set('groups', groups.join(','));

  const { data, isLoading } = useQuery({
    queryKey: ['contacts-pick', qs.toString()],
    queryFn: async () => (await apiFetch('GET', `/contacts?${qs.toString()}`)).data as { data: Contact[]; total: number },
  });
  const list = data?.data ?? [];
  const total = data?.total ?? 0;

  const allOn = sel.mode === 'all';
  const setAll = (on: boolean) => {
    if (on) { setSel({ mode: 'all', ids: new Set(), total, filter }); return; }
    setPicked(new Map());
    setSel({ mode: 'ids', ids: new Set(), total: 0, filter: {} });
  };
  const toggleId = (c: Contact) => {
    if (allOn) return;
    const ids = new Set(sel.ids);
    const pk = new Map(picked);
    if (ids.has(c.id)) { ids.delete(c.id); pk.delete(c.id); }
    else { ids.add(c.id); pk.set(c.id, c); }
    setPicked(pk);
    setSel({ mode: 'ids', ids, total: 0, filter: {} });
  };
  const removeId = (id: string) => {
    const ids = new Set(sel.ids); ids.delete(id);
    const pk = new Map(picked); pk.delete(id);
    setPicked(pk);
    setSel({ mode: 'ids', ids, total: 0, filter: {} });
  };
  const selCount = allOn ? total : sel.ids.size;
  // Na aba "Todos", não exibimos mais os já selecionados (em modo ids).
  const visible = allOn ? list : list.filter((c) => !sel.ids.has(c.id));
  // Mantém o total/filter do modo "todos" em sincronia com a busca (fora do render).
  useEffect(() => {
    if (allOn) setSel({ mode: 'all', ids: new Set(), total, filter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOn, total, JSON.stringify(filter)]);

  const toggleChip = (arr: string[], set: (v: string[]) => void, key: string) =>
    set(arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key]);

  return (
    <Card><CardContent className="flex flex-col gap-4 p-5">
      <div className="flex items-center gap-2">
        <Users className="size-4 text-primary" />
        <h2 className="text-base font-semibold">{t('campaigns.destinatarios')}</h2>
        <span className="ml-auto flex items-center gap-1 text-xs text-emerald-600"><ShieldCheck className="size-3.5" /> {t('campaigns.soValidos')}</span>
      </div>

      {existingCount !== undefined && !allOn && sel.ids.size === 0 && (
        <p className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <b>{existingCount}</b> {t('campaigns.jaSelecionadosPre')} <b>{t('campaigns.substituir')}</b> {t('campaigns.jaSelecionadosPos')}
        </p>
      )}

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'all' | 'selected')}>
        <TabsList className="w-full">
          <TabsTrigger value="all" className="flex-1">{t('campaigns.abaTodos')}</TabsTrigger>
          <TabsTrigger value="selected" className="flex-1">{t('campaigns.abaSelecionados')}{selCount > 0 ? ` (${selCount})` : ''}</TabsTrigger>
        </TabsList>

        {/* ABA: Todos — busca/filtros, "selecionar todos", e a lista SEM os já escolhidos */}
        <TabsContent value="all" className="flex flex-col gap-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('campaigns.buscarContato')} className="pl-8" />
          </div>
          {(groupDefs.length > 0 || tagDefs.length > 0) && (
            <div className="flex flex-col gap-2">
              {groupDefs.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-medium text-muted-foreground">{t('campaigns.grupos')}</span>
                  {groupDefs.map((g) => <Chip key={g.id} on={groups.includes(g.key)} onClick={() => toggleChip(groups, setGroups, g.key)} label={g.name || g.key} />)}
                </div>
              )}
              {tagDefs.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-medium text-muted-foreground">{t('campaigns.tags')}</span>
                  {tagDefs.map((t) => <Chip key={t.id} on={tags.includes(t.key)} onClick={() => toggleChip(tags, setTags, t.key)} label={t.name || t.key} />)}
                </div>
              )}
            </div>
          )}

          {/* Selecionar todos */}
          <label className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2 text-sm">
            <input type="checkbox" checked={allOn} onChange={(e) => setAll(e.target.checked)} className="size-4 accent-[#E0780C]" />
            <span>{t('campaigns.selecionar')} <b>todos os {total}</b> {t('campaigns.casamFiltro')}</span>
          </label>

          {/* Lista (sem os já selecionados) */}
          <div className="max-h-72 overflow-y-auto rounded-md border">
            {isLoading ? (
              <div className="space-y-2 p-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
            ) : visible.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {list.length > 0 && !allOn ? 'Todos os contatos deste filtro já foram selecionados.' : 'Nenhum contato válido para este filtro.'}
              </p>
            ) : (
              <ul className="divide-y">
                {visible.map((c) => (
                  <li key={c.id}>
                    <label className={`flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-accent ${allOn ? 'opacity-60' : ''}`}>
                      <input type="checkbox" checked={allOn} disabled={allOn} onChange={() => toggleId(c)} className="size-4 accent-[#E0780C]" />
                      <span className="min-w-0 flex-1 truncate font-medium">{c.name || formatPhone(c.phone)}</span>
                      <span className="shrink-0 font-mono text-xs text-muted-foreground">{formatPhone(c.phone)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {visible.length > 0 && total > list.length && !allOn && (
            <p className="text-xs text-muted-foreground">Mostrando {visible.length} de {total}. Refine o filtro ou use “selecionar todos”.</p>
          )}
        </TabsContent>

        {/* ABA: Selecionados — lista os escolhidos e permite remover */}
        <TabsContent value="selected" className="flex flex-col gap-3">
          {allOn ? (
            <div className="flex flex-col gap-3 rounded-md border p-4 text-sm">
              <p><b>Todos os {total}</b> {t('campaigns.todosSelecionados')}</p>
              <Button variant="outline" size="sm" className="self-start" onClick={() => setAll(false)}>{t('campaigns.limparSelecao')}</Button>
            </div>
          ) : sel.ids.size === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('campaigns.nenhumSelecionadoPre')} <b>{t('campaigns.abaTodos')}</b> {t('campaigns.nenhumSelecionadoPos')}</p>
          ) : (
            <div className="max-h-96 overflow-y-auto rounded-md border">
              <ul className="divide-y">
                {[...picked.values()].map((c) => (
                  <li key={c.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <span className="min-w-0 flex-1 truncate font-medium">{c.name || formatPhone(c.phone)}</span>
                    <span className="shrink-0 font-mono text-xs text-muted-foreground">{formatPhone(c.phone)}</span>
                    <button type="button" onClick={() => removeId(c.id)} className="shrink-0 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" aria-label={t('campaigns.remover')}>
                      <XIcon className="size-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </CardContent></Card>
  );
}

function Chip({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick}
      className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${on ? 'border-primary bg-primary/10 text-foreground' : 'border-input text-muted-foreground hover:bg-accent'}`}>
      {label}
    </button>
  );
}

// ---- Dashboard em tempo real ----------------------------------------------

function LiveDashboard({ count, numbers, seconds }: { count: number; pacing: string; numbers: number; seconds: number }) {
  const t = useT();
  return (
    <Card><CardContent className="flex flex-col gap-4 p-5">
      <h2 className="text-sm font-semibold text-muted-foreground">{t('campaigns.resumo')}</h2>
      <div className="flex items-end gap-2">
        <span className="text-4xl font-bold tabular-nums">{count}</span>
        <span className="mb-1 text-sm text-muted-foreground">{t('campaigns.contatosSelecionados')}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-md bg-muted/40 p-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Smartphone className="size-3.5" /> {t('campaigns.numeros')}</div>
          <p className="text-lg font-bold tabular-nums">{numbers}</p>
        </div>
        <div className="rounded-md bg-muted/40 p-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="size-3.5" /> {t('campaigns.tempoEstimado')}</div>
          <p className="text-lg font-bold">{count > 0 ? humanTime(seconds) : '—'}</p>
        </div>
      </div>
      {count > 0 && numbers === 0 && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Nenhum número elegível agora (aquecendo ou desconectado). O disparo espera um número ficar pronto.
        </p>
      )}
    </CardContent></Card>
  );
}

// ---- Painel de números (elegibilidade / aquecimento) -----------------------

interface NumberElig {
  instance_id: string; phone: string; nickname?: string; status: string;
  connected_days: number; health: number; eligible: boolean; reason?: string;
}
interface Eligibility {
  warmup_days: number; min_numbers: number; eligible_count: number;
  can_dispatch: boolean; reason: string; numbers: NumberElig[];
}

function NumbersPanel({ elig }: { elig?: Eligibility }) {
  const t = useT();
  if (!elig) return null;
  const { numbers, warmup_days, min_numbers, eligible_count, can_dispatch, reason } = elig;
  const daysLabel = (d: number) => (d === 0 ? 'hoje' : d === 1 ? 'há 1 dia' : `há ${d} dias`);
  return (
    <Card><CardContent className="flex flex-col gap-3 p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-muted-foreground">{t('campaigns.seusNumeros')}</h2>
        <span className="text-xs text-muted-foreground">{eligible_count}/{min_numbers} elegíveis</span>
      </div>

      {/* Veredito de disparo */}
      {can_dispatch ? (
        <p className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          Pronto para disparar — {eligible_count} número(s) elegível(is).
        </p>
      ) : (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">{reason}</p>
      )}

      {/* Lista por número */}
      {numbers.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('campaigns.semNumero')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {numbers.map((n) => (
            <li key={n.instance_id} className="flex items-center gap-2.5 rounded-md border p-2.5">
              <span className={`size-2.5 shrink-0 rounded-full ${n.eligible ? 'bg-emerald-500' : n.health <= 0 ? 'bg-red-500' : n.status === 'connected' ? 'bg-amber-500' : 'bg-zinc-400'}`} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{n.nickname || n.phone || '—'}</p>
                <p className="truncate text-xs text-muted-foreground">
                  Conectado {daysLabel(n.connected_days)}
                  {n.eligible ? ' · elegível' : n.reason ? ` · ${n.reason}` : ''}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[0.7rem] leading-relaxed text-muted-foreground">
        Aquecimento exigido: {warmup_days} dia(s). Mínimo de {min_numbers} número(s) para disparar. Rotacionar entre vários números reduz o risco de bloqueio.
      </p>
    </CardContent></Card>
  );
}

// ---- Preview no celular ----------------------------------------------------

function PhonePreview({ text, image }: { text: string; image: string | null }) {
  const t = useT();
  return (
    <Card><CardContent className="flex flex-col gap-2 p-5">
      <h2 className="text-sm font-semibold text-muted-foreground">{t('campaigns.previaTitulo')}</h2>
      <div className="rounded-2xl border bg-[#e5ddd5] p-3" style={{ backgroundImage: 'radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px)', backgroundSize: '12px 12px' }}>
        <div className="ml-auto max-w-[85%] overflow-hidden rounded-lg rounded-tr-none bg-[#dcf8c6] shadow-sm">
          {image && <img src={image} alt="" className="max-h-48 w-full object-cover" />}
          <div className="px-2.5 py-1.5">
            <p className="whitespace-pre-wrap break-words text-sm text-zinc-800">
              {text || <span className="text-zinc-400">{t('campaigns.previaVazia')}</span>}
            </p>
            <div className="mt-0.5 flex items-center justify-end gap-1 text-[0.65rem] text-zinc-500">
              12:34 <CheckCheck className="size-3 text-sky-500" />
            </div>
          </div>
        </div>
      </div>
      <p className="text-[0.7rem] text-muted-foreground">{t('campaigns.previaNota')}</p>
    </CardContent></Card>
  );
}

// ---- Detalhe da campanha (KPIs ao vivo + controles) -----------------------

function CampaignDetail({ id, onBack, onChange }: { id: string; onBack: () => void; onChange: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const onFreeQuota = useFreeQuotaToast();
  const [busy, setBusy] = useState(false);
  const [dry, setDry] = useState<DryRun | null>(null);

  const { data: detail } = useQuery({
    queryKey: ['campaign', id],
    queryFn: async () => (await apiFetch('GET', `/campaigns/${id}`)).data as { campaign: Campaign; stats: Stats },
    refetchInterval: (q) => (q.state.data?.campaign?.status === 'running' ? 4000 : false),
  });
  const campaign = detail?.campaign;
  const s = detail?.stats;
  const refresh = () => { qc.invalidateQueries({ queryKey: ['campaign', id] }); onChange(); };

  const action = async (act: string) => {
    setBusy(true);
    const res = await apiFetch('POST', `/campaigns/${id}/${act}`);
    setBusy(false);
    if (!res.ok) {
      // Franquia do Free esgotada: mensagem do servidor + upgrade, não um toast cru.
      if (onFreeQuota(res)) return;
      toast.error((res.data as { message?: string })?.message ?? 'Falha na ação.'); return;
    }
    const waiting = (res.data as { waiting?: string })?.waiting;
    if (waiting) toast.warning(waiting.charAt(0).toUpperCase() + waiting.slice(1) + '.');
    else toast.success(t('campaigns.feito'));
    refresh();
  };
  const runDryRun = async () => {
    const res = await apiFetch('POST', `/campaigns/${id}/dry-run`);
    if (res.ok) setDry(res.data as DryRun);
  };

  if (!campaign) return <div className="p-10 text-center"><Loader2 className="mx-auto size-6 animate-spin text-muted-foreground" /></div>;

  const total = s?.total ?? 0;
  const done = (s?.sent ?? 0) + (s?.failed ?? 0) + (s?.suppressed ?? 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const canStart = campaign.status === 'draft' || campaign.status === 'paused';
  const canControl = ['running', 'scheduled', 'draft', 'paused'].includes(campaign.status);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} aria-label={t('campaigns.voltar')}><ArrowLeft className="size-4" /></Button>
        <h1 className="text-lg font-semibold">{campaign.name || t('campaigns.semNome')}</h1>
        <Badge className={STATUS_TONE[campaign.status] ?? ''}>{STATUS_KEY[campaign.status] ? t(STATUS_KEY[campaign.status]) : campaign.status}</Badge>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={runDryRun}><FlaskConical className="size-4" /> {t('campaigns.simular')}</Button>
          {canStart && <Button size="sm" disabled={busy} onClick={() => action('start')}><Play className="size-4" /> {t('campaigns.iniciar')}</Button>}
          {campaign.status === 'running' && <Button variant="outline" size="sm" disabled={busy} onClick={() => action('pause')}><Pause className="size-4" /> {t('campaigns.pausar')}</Button>}
          {campaign.status === 'paused' && <Button size="sm" disabled={busy} onClick={() => action('resume')}><Play className="size-4" /> {t('campaigns.retomar')}</Button>}
          {canControl && <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" disabled={busy} onClick={() => action('cancel')}><XIcon className="size-4" /> {t('campaigns.cancelar')}</Button>}
        </div>
      </div>

      {campaign.waiting && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          <Clock className="mt-0.5 size-4 shrink-0" />
          <span>Aguardando: {campaign.waiting}. Fora da janela nada é enviado — é proteção contra bloqueio do WhatsApp.</span>
        </div>
      )}

      <Card><CardContent className="flex flex-col gap-4 p-5">
        <div>
          <div className="mb-1 flex justify-between text-sm text-muted-foreground"><span>{done}/{total} processados</span><span>{pct}%</span></div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          <Kpi label={t('campaigns.kpiTotal')} value={total} />
          <Kpi label={t('campaigns.kpiEnviados')} value={s?.sent ?? 0} tone="text-emerald-600" />
          <Kpi label={t('campaigns.kpiEntregues')} value={s?.delivered ?? 0} tone="text-sky-600" />
          <Kpi label={t('campaigns.kpiLidos')} value={s?.read ?? 0} tone="text-violet-600" />
          <Kpi label={t('campaigns.kpiPendentes')} value={s?.pending ?? 0} tone="text-amber-600" />
          <Kpi label={t('campaigns.kpiFalhas')} value={s?.failed ?? 0} tone="text-red-600" />
        </div>
      </CardContent></Card>

      {dry && (
        <Card><CardContent className="flex flex-col gap-2 p-5 text-sm">
          <h2 className="flex items-center gap-2 font-semibold"><FlaskConical className="size-4" /> {t('campaigns.simulacao')}</h2>
          <p className="text-muted-foreground">{t('campaigns.simNumeros')} <b>{dry.numbers_available}</b> {t('campaigns.simVariacoes')} <b>{dry.variations}</b> {t('campaigns.simDuracao')} <b>{dry.estimated_human}</b></p>
          {dry.missing_variables.length > 0 && <p className="text-amber-700">Variáveis sem valor em alguns contatos: {dry.missing_variables.join(', ')}</p>}
          {dry.warnings.map((w, i) => <p key={i} className="flex items-center gap-1.5 text-amber-700"><RefreshCw className="size-3.5" /> {w}</p>)}
        </CardContent></Card>
      )}

      <RecipientsList id={id} live={campaign.status === 'running'} />
    </div>
  );
}

// ---- Detalhamento por contato ---------------------------------------------

interface RecipientRow {
  id: string; phone: string; contact_name?: string; status: string; delivery?: string; last_error?: string;
}

/** Estado de entrega por contato, combinando status do destinatário + recibo. */
function recipientState(r: RecipientRow): { labelKey: string; tone: string } {
  if (r.status === 'suppressed') return { labelKey: 'campaigns.estSuprimido', tone: 'bg-zinc-200 text-zinc-600' };
  if (r.status === 'failed') return { labelKey: 'campaigns.estFalhou', tone: 'bg-red-100 text-red-700' };
  if (r.status === 'pending' || r.status === 'claimed') return { labelKey: 'campaigns.estPendente', tone: 'bg-amber-100 text-amber-700' };
  if (r.delivery === 'read') return { labelKey: 'campaigns.estLido', tone: 'bg-violet-100 text-violet-700' };
  if (r.delivery === 'delivered') return { labelKey: 'campaigns.estEntregue', tone: 'bg-sky-100 text-sky-700' };
  return { labelKey: 'campaigns.estEnviado', tone: 'bg-emerald-100 text-emerald-700' };
}

const RECIP_FILTERS: { key: string; labelKey: string; match: (r: RecipientRow) => boolean }[] = [
  { key: 'all', labelKey: 'campaigns.abaTodos', match: () => true },
  { key: 'sent', labelKey: 'campaigns.kpiEnviados', match: (r) => r.status === 'sent' },
  { key: 'delivered', labelKey: 'campaigns.kpiEntregues', match: (r) => r.delivery === 'delivered' || r.delivery === 'read' },
  { key: 'read', labelKey: 'campaigns.kpiLidos', match: (r) => r.delivery === 'read' },
  { key: 'pending', labelKey: 'campaigns.kpiPendentes', match: (r) => r.status === 'pending' || r.status === 'claimed' },
  { key: 'failed', labelKey: 'campaigns.kpiFalhas', match: (r) => r.status === 'failed' },
];

function RecipientsList({ id, live }: { id: string; live: boolean }) {
  const t = useT();
  const [filter, setFilter] = useState('all');
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['campaign', id, 'recipients'],
    queryFn: async (): Promise<RecipientRow[]> =>
      ((await apiFetch('GET', `/campaigns/${id}/recipients?limit=500`)).data as { data?: RecipientRow[] })?.data ?? [],
    refetchInterval: live ? 5000 : false,
  });
  const active = RECIP_FILTERS.find((f) => f.key === filter) ?? RECIP_FILTERS[0];
  const shown = rows.filter(active.match);

  return (
    <Card><CardContent className="flex flex-col gap-3 p-5">
      <div className="flex flex-wrap items-center gap-2">
        <Users className="size-4 text-primary" />
        <h2 className="text-base font-semibold">{t('campaigns.detalhamento')}</h2>
        <span className="text-xs text-muted-foreground">({rows.length})</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {RECIP_FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${filter === f.key ? 'border-primary bg-primary/10 text-foreground' : 'border-input text-muted-foreground hover:bg-accent'}`}>
            {t(f.labelKey)}
          </button>
        ))}
      </div>
      <div className="max-h-96 overflow-y-auto rounded-md border">
        {isLoading ? (
          <div className="space-y-2 p-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : shown.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('campaigns.semDestinatarios')}</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {shown.map((r) => {
                const st = recipientState(r);
                return (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="px-3 py-2">
                      <div className="truncate font-medium">{r.contact_name || formatPhone(r.phone)}</div>
                      {r.contact_name && <div className="font-mono text-xs text-muted-foreground">{formatPhone(r.phone)}</div>}
                      {r.status === 'failed' && r.last_error && <div className="truncate text-xs text-red-600" title={r.last_error}>{r.last_error}</div>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Badge className={st.tone}>{t(st.labelKey)}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </CardContent></Card>
  );
}
